import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/lib/ConfigProvider'
import { useOnboarding } from '@/onboarding/OnboardingProvider'
import {
  decisionDate,
  fetchConsents,
  lawfulBasisLabel,
  recordConsent,
  standing,
} from './consents'
import type {
  Consent,
  ConsentAction,
  ConsentsState,
  RecordConsentResult,
} from './consents'
import { languageName } from './locale'
import './PrivacyConsents.css'

/**
 * What we ask this customer's permission for, and what they have said
 * (`#118`, backend `GET /v1/privacy/consents`).
 *
 * # The wording is never ours; the decision is always theirs
 *
 * It shows a record AND changes one, and what it will not do is a decision
 * rather than a gap:
 *
 *   - **No wording is written here.** Not a placeholder, not a draft. The
 *     backend hashes the bytes counsel signed off (`text_sha256`), so invented
 *     text does not stand in for a missing document — it produces a recorded
 *     acceptance of words nobody approved, in an append-only table, read later
 *     by a supervisory authority. Every sentence of legal text on this screen
 *     comes out of the response and is rendered verbatim.
 *   - **The toggle records, and records the wording it was shown against.**
 *     `PUT /v1/privacy/consents` is built here now (`#126`). Every decision
 *     echoes back the `version` and `text_sha256` of the item that was
 *     rendered, so an answer is an answer to a specific text rather than to a
 *     purpose — and a screen left open while the wording moves is refused
 *     `409 text_changed` with no row written, re-read, and asked again.
 *
 *     A DISCLOSURE GETS A BUTTON AND NOT A SWITCH. `granted: false` on a
 *     purpose whose `choice` is false is answered `409 not_a_choice`, and the
 *     contract's reason is the point: "turning it off documents that we ignored
 *     them." There is no lawful "no" to draw, so none is drawn.
 *
 *     The affirmative act is taken from the control the customer actually used
 *     — `button_pressed` for a disclosure, `checkbox_ticked` / `checkbox_cleared`
 *     for a switch — never defaulted. Art. 7(2): "they agreed" is not a record;
 *     "they ticked an unticked box" is.
 *
 * # The empty case is not the live case, and that correction is #124
 *
 * This module said it was: no deployment has published any wording
 * (`go-wallet#523`), so `{"locale":"en","consents":[]}` was taken to be what
 * the screen renders today. That answer requires the route to be MOUNTED, and
 * it is not. `cmd/walletd/privacy.go` returns before mounting anything unless
 * object storage is configured — deliberately, "all three halves or none" —
 * and `WALLET_OBJECT_STORE_ENDPOINT` is empty in the staging AND production
 * overlays, with `deployments/SECRETS.md` saying "no bucket exists yet" for
 * both.
 *
 * So the state our deployments are actually in is `not_offered`, and the screen
 * used to render it as "we could not read this" with a retry — a transient
 * wording and a dead control for a condition that is neither. `nothing_published`
 * stays exactly as it was written; it becomes the live case the day the surface
 * is mounted.
 */
export function PrivacyConsents() {
  const api = useApi()
  const { state: onboarding } = useOnboarding()
  const [state, setState] = useState<ConsentsState>({ status: 'loading' })

  // The read is GATED ON THERE BEING A CUSTOMER, not merely on being signed in.
  //
  // The handler refuses `CustomerID == 0` with a 401, and `lib/api` turns every
  // 401 into an `onUnauthenticated` notification, which re-asks Ory. So calling
  // this for a signed-in person who has not registered — the state every new
  // customer passes through — would fire a session round-trip on each render of
  // Settings, for a situation that is entirely normal and that we can already
  // read locally.
  const registered = onboarding.status === 'ready'

  // Same guard as Balances (#57): overlapping reads are not reachable through
  // this component today because `load` clears the retry control before
  // awaiting, but that is a rendering detail rather than a guarantee.
  const generation = useRef(0)

  const load = useCallback(async () => {
    const mine = ++generation.current
    setState({ status: 'loading' })
    const next = await fetchConsents(api)
    if (mine !== generation.current) return
    setState(next)
  }, [api])

  useEffect(() => {
    if (!registered) return
    void load()
  }, [load, registered])

  // Keyed by purpose so two cards cannot share one spinner or one error. A
  // customer answering the marketing switch must not see the terms card go
  // busy, and an error on one is not an error on the other.
  const [pending, setPending] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<
    Record<string, RecordConsentResult | undefined>
  >({})

  const decide = useCallback(
    async (consent: Consent, granted: boolean, action: ConsentAction) => {
      setPending(consent.purpose)
      setOutcome((o) => ({ ...o, [consent.purpose]: undefined }))
      const result = await recordConsent(api, consent, granted, action)
      setPending(null)
      setOutcome((o) => ({ ...o, [consent.purpose]: result }))
      // Re-read on anything the backend accepted or superseded, so the card
      // states what the RECORD says rather than what this client believes it
      // just did. `text_changed` re-reads for the opposite reason: the wording
      // on screen is stale, and the customer must be asked against the new one.
      if (result.status === 'recorded' || result.status === 'text_changed') {
        await load()
      }
    },
    [api, load],
  )

  return (
    <section className="consents" aria-labelledby="consents-title">
      <h2 className="consents__title" id="consents-title">
        Privacy and consent
      </h2>
      <p className="consents__lede">
        What we ask your permission for, the wording you were shown, and what
        you have said about it.
      </p>

      {/*
        Four absences, four sentences. "Nothing is being asked of you", "your
        wallet is not open yet" and "we could not look" are the same empty panel
        and mean entirely different things — the distinction #22 and #101 were
        filed for.
      */}
      {!registered ? (
        <NotYetACustomer onboarding={onboarding.status} />
      ) : state.status === 'loading' ? (
        <p className="consents__muted">Reading what has been asked of you…</p>
      ) : state.status === 'unregistered' ? (
        <NotYetACustomer onboarding="unregistered" />
      ) : state.status === 'unavailable' ? (
        <div className="consents__notice" role="status">
          <p className="consents__noticeTitle">We could not read this.</p>
          {/*
            Deliberately NOT 'nothing is being asked of you'. We do not know
            that, and saying it would be a false statement about what somebody
            has agreed to — the exact class of claim this screen exists to make
            accurately.
          */}
          <p className="consents__muted">{state.reason}</p>
          <button
            type="button"
            className="consents__ghost"
            onClick={() => void load()}
          >
            Try again
          </button>
        </div>
      ) : state.status === 'not_offered' ? (
        <div className="consents__notice" role="status">
          {/*
            NOT "nothing is being asked of you" — we have not been told that and
            do not know it — and NOT "we could not read this", which invites a
            retry that cannot succeed. This deployment does not serve the route,
            and the honest sentence is the one `Welcome.tsx` already uses for a
            refusal a customer cannot retry their way out of (#75).
          */}
          <p className="consents__noticeTitle">We cannot show you this here.</p>
          <p className="consents__muted">
            This is something we have to fix at our end — there is nothing for
            you to do, and trying again will not change it. It does not mean
            anything has changed about what you have agreed to.
          </p>
        </div>
      ) : state.status === 'nothing_published' ? (
        <div className="consents__notice" role="status">
          <p className="consents__noticeTitle">
            Nothing is being asked of you.
          </p>
          <p className="consents__muted">
            No terms, privacy notice or other consent wording is published here,
            so there is nothing for you to agree to and nothing for us to
            record. If that changes, it will appear on this screen.
          </p>
        </div>
      ) : (
        <>
          <ul className="consents__list">
            {state.consents.map((c) => (
              <ConsentCard
                key={c.purpose}
                consent={c}
                busy={pending === c.purpose}
                // Any card being answered disables the others. Two decisions in
                // flight against one document is a race whose loser is a
                // customer's recorded answer.
                blocked={pending !== null && pending !== c.purpose}
                outcome={outcome[c.purpose]}
                onDecide={decide}
              />
            ))}
          </ul>
          {state.locale !== '' && (
            <p className="consents__foot">
              This wording is published in {languageName(state.locale)}.
            </p>
          )}
        </>
      )}
    </section>
  )
}

function NotYetACustomer({ onboarding }: { onboarding: string }) {
  return (
    <div className="consents__notice" role="status">
      <p className="consents__noticeTitle">
        {onboarding === 'unavailable'
          ? 'We could not tell whether this applies to you yet.'
          : 'This applies once your wallet is open.'}
      </p>
      <p className="consents__muted">
        {onboarding === 'unavailable'
          ? 'We could not read where your application stands, so we have not asked what has been recorded for you.'
          : onboarding === 'loading'
            ? 'Checking where your application stands…'
            : 'Consent is recorded against a wallet. Yours has not been opened yet, so there is nothing recorded against it.'}
      </p>
      <Link className="consents__link" to="/welcome">
        See where your checks stand
      </Link>
    </div>
  )
}

function ConsentCard({
  consent: c,
  busy,
  blocked,
  outcome,
  onDecide,
}: {
  consent: Consent
  busy: boolean
  blocked: boolean
  outcome: RecordConsentResult | undefined
  onDecide: (c: Consent, granted: boolean, action: ConsentAction) => void
}) {
  const s = standing(c)
  // `null` when there is no decision, and also when there is one whose
  // instant this app cannot read. Both drop the clause rather than putting
  // `Invalid Date` next to a statement about what somebody agreed to.
  const when = c.decidedAt === undefined ? null : decisionDate(c.decidedAt)

  return (
    <li className={`consents__card consents__card--${s.tone}`}>
      <div className="consents__head">
        <span className="consents__summary">{c.summary}</span>
        <span className={`consents__chip consents__chip--${s.tone}`}>
          {s.label}
        </span>
      </div>

      <p className="consents__meta">
        <span className="consents__mono">{c.purpose}</span>
        <span aria-hidden="true"> · </span>
        <span>Version {c.version}</span>
        <span aria-hidden="true"> · </span>
        <span>
          We do this on the basis of {lawfulBasisLabel(c.lawfulBasis)}
        </span>
      </p>

      {/*
        The history, and it is only shown when it says something. "You answered
        version 2, and this is version 3" is the single fact that makes
        `needsAnswer` comprehensible rather than arbitrary — without it a
        customer who remembers agreeing is being told, apparently at random,
        that they have not.
      */}
      {c.needsAnswer && c.answered && (
        <p className="consents__changed">
          You answered
          {c.answeredVersion === undefined
            ? ' an earlier version'
            : ` version ${c.answeredVersion}`}
          {when === null ? '' : ` on ${when}`}. This wording has changed since, so
          that answer does not carry over to it.
        </p>
      )}
      {!c.needsAnswer && when !== null && (
        <p className="consents__meta">You answered this on {when}.</p>
      )}

      {/*
        The wording, verbatim and always visible.

        Not behind a disclosure control. The contract calls `body` "the full
        wording, exactly as it must be shown", and a legal text a customer has
        to click to reveal is one they can be said not to have been shown.
        `white-space: pre-wrap` keeps the author's own line breaks, because the
        bytes are what was hashed.
      */}
      <div className="consents__body">{c.body}</div>

      {/*
        SAID FOR A SETTLED CHOICE TOO, NOT ONLY FOR AN OUTSTANDING ONE (#125).

        The #116 / §9 posture is to say the control is not built rather than to
        draw one that does nothing, and law 20 adds that a DISABLED one is worse
        than none: it says "you lack permission", which is false where the
        capability has not been built for anybody.

        This sentence used to be keyed on `needsAnswer` alone, which never fires
        for the case Art. 7(3) is actually about — a consent the customer HAS
        granted against the current wording, which they may now want to
        withdraw. That card said "You allowed this" and nothing else, so the one
        item somebody might have opened this screen to switch off was the one
        item silent about being unable to.

        A settled DISCLOSURE gets no line: there is nothing to withdraw, and a
        refusal to accept one is answered `409 not_a_choice`.
      */}
      {/*
        THE CONTROL (#126).

        A DISCLOSURE GETS A BUTTON, A CHOICE GETS A SWITCH, and that is the
        contract rather than a style: `granted: false` on a purpose whose
        `choice` is false is answered `409 not_a_choice`. Drawing a switch there
        would offer an action that cannot be taken and, worse, imply the
        processing rests on their permission when it does not.

        A settled disclosure gets nothing at all — there is no second acceptance
        to give, and no withdrawal to offer.
      */}
      {c.choice ? (
        <div className="consents__control">
          <label className="consents__toggle">
            <input
              type="checkbox"
              checked={c.granted}
              disabled={busy || blocked}
              onChange={(e) =>
                onDecide(
                  c,
                  e.currentTarget.checked,
                  // The act, taken from the control that was used. Never
                  // inferred from the resulting value: Art. 7(2) records what
                  // they DID, and "ticked" and "cleared" are different acts.
                  e.currentTarget.checked
                    ? 'checkbox_ticked'
                    : 'checkbox_cleared',
                )
              }
            />
            <span>
              {c.needsAnswer
                ? 'Tick to agree to this wording'
                : c.granted
                  ? 'You allow this. Clear the box to withdraw it.'
                  : 'You do not allow this. Tick the box to agree.'}
            </span>
          </label>
        </div>
      ) : c.needsAnswer ? (
        <div className="consents__control">
          <button
            type="button"
            className="consents__accept"
            disabled={busy || blocked}
            onClick={() => onDecide(c, true, 'button_pressed')}
          >
            {busy ? 'Recording…' : 'I accept this wording'}
          </button>
          {/*
            Said next to the button rather than after the fact. A required
            disclosure is not a choice, and a customer is entitled to know that
            before they press rather than to discover it by looking for a way
            out afterwards.
          */}
          <p className="consents__muted">
            We need this to provide the service, so there is no version of it to
            switch off — accepting records that you were shown this wording.
          </p>
        </div>
      ) : null}

      {outcome !== undefined && <Outcome result={outcome} choice={c.choice} />}
    </li>
  )
}

/**
 * What happened, in the customer's terms.
 *
 * Every branch says what it means for THEM and what to do next. `text_changed`
 * is the one that must not read as a failure: nothing went wrong, the document
 * moved, and the screen has already re-read it.
 */
function Outcome({
  result,
  choice,
}: {
  result: RecordConsentResult
  choice: boolean
}) {
  switch (result.status) {
    case 'recorded':
      return (
        <p className="consents__ok" role="status">
          {result.granted
            ? 'Recorded. We have your agreement to this wording.'
            : 'Recorded. You have withdrawn this, and we will stop.'}
        </p>
      )
    case 'text_changed':
      return (
        <p className="consents__warn" role="status">
          This wording changed while the page was open, so nothing was recorded
          against the version you were reading. The new wording is above —
          please read it and answer again.
        </p>
      )
    case 'not_a_choice':
      return (
        <p className="consents__warn" role="status">
          {choice
            ? 'We could not record that as a withdrawal.'
            : 'This one is not a choice we can offer to switch off — we need it to provide the service, so there is nothing to withdraw.'}
        </p>
      )
    case 'unknown_purpose':
      return (
        <p className="consents__warn" role="status">
          This is no longer being asked. Nothing was recorded, and there is
          nothing for you to do.
        </p>
      )
    case 'not_offered':
    case 'unregistered':
      return (
        <p className="consents__warn" role="status">
          We could not record that here. Nothing has changed about what we hold.
        </p>
      )
    case 'unavailable':
      return (
        <p className="consents__warn" role="status">
          We could not record that just now, and nothing was written — your
          answer is unchanged. Please try again.
        </p>
      )
  }
}
